import { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Loader from "../common/Loader";
import { guestRoutes, standardRoutes } from "../../utils/routes";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { primaryColor } from "../styled/Theme";
import { useIdleTimer } from "react-idle-timer";
import { IDLE_TIMEOUT, IDLE_WARNING_TIME } from "../../utils/constants";
import { AppRoute } from "../../types/common";
import toast from "react-hot-toast";
import { useGlobalContext } from "../../context/GlobalContext";

const MainNavigator = () => {
  document.documentElement.style.setProperty("--selected-color", primaryColor);

  const { loggedIn, gettingAuth, user, logout } = useAuth();
  const { updateData } = useGlobalContext();

  const { reset } = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    promptBeforeIdle: IDLE_WARNING_TIME,
    onPrompt: () => {
      if (loggedIn) {
        toast.custom(
          (t) => (
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                maxWidth: "400px",
                border: "2px solid #ff9800",
              }}
            >
              <div style={{ marginBottom: "15px", textAlign: "center" }}>
                <h3 style={{ margin: "0 0 10px 0", color: "#333" }}>
                  Session Expiring Soon
                </h3>
                <p
                  style={{
                    fontSize: "16px",
                    marginBottom: "8px",
                    color: "#333",
                  }}
                >
                  Your session will expire in <strong>1 minute</strong> due to
                  inactivity.
                </p>
                {/* <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
                  Click anywhere or move your mouse to stay logged in.
                </p> */}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                    logout();
                  }}
                  style={{
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Logout
                </button>
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                    reset();
                  }}
                  style={{
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Stay Logged In
                </button>
              </div>
            </div>
          ),
          {
            duration: Infinity,
            position: "top-center",
          },
        );
      }
    },
    onIdle: () => {
      if (loggedIn) {
        toast.dismiss();
        // Close all modals before logout
        updateData({
          openGenerateApiKey: false,
          openIframeKeyGeneration: false,
          openStaticQRCode: false,
          openDisbursementSettings: false,
          openUSSDHealthCheck: false,
          openDisbursementHealthCheck: false,
          openCheckPaymentStatus: false,
          openInterestStatement: false,
          openSettlementEligibilityReview: false,
        });
        logout();
      }
    },
    onActive: () => {
      toast.dismiss();
    },
  });

  if (gettingAuth) return <Loader />;

  // --------------------- LOGGED-IN ROUTES ---------------------
  if (loggedIn) {
    return (
      <DashboardLayout>
        <Suspense fallback={<Loader />}>
          <Routes>
            {standardRoutes
              .filter((route) => {
                if (user?.must_change_password) {
                  return route.path === "/change-password";
                }
                return true;
              })
              .map(({ path, component, children }: AppRoute, i) => (
                <Route path={path} element={component} key={i}>
                  {children &&
                    children.map((child, j) => (
                      <Route
                        key={`${i}-${j}`}
                        index={child.index}
                        path={child.path}
                        element={child.component}
                      />
                    ))}
                </Route>
              ))}
            <Route
              path="*"
              element={
                <Navigate
                  to={user?.must_change_password ? "/change-password" : "/"}
                  replace
                />
              }
            />
          </Routes>
        </Suspense>
      </DashboardLayout>
    );
  }

  // --------------------- GUEST ROUTES ---------------------
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {guestRoutes.map(({ path, component, children }, i) => (
          <Route path={path} element={component} key={i}>
            {children &&
              children.map((child, j) => (
                <Route
                  key={`${i}-${j}`}
                  index={child.index}
                  path={child.path}
                  element={child.component}
                />
              ))}
          </Route>
        ))}

        {/* fallback to home */}
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
    </Suspense>
  );
};

export default MainNavigator;
